import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import SignIn from './components/SignIn';
import Security from './components/Security';
import Logout from './components/Logout';
import SwitchTenant from './components/SwitchTenant';
import TenantPicker from './components/TenantPicker';
import { getSession, refreshSession, isDeviceTrusted } from '@darice/auth-client';

const THIRTY_MIN_MS = 30 * 60 * 1000;

function Home() {
  return (
    <main>
      <h1>Darice Auth</h1>
      <p>Signed in. Use the tenant picker to switch workspaces.</p>
    </main>
  );
}

// 2026-07-28 ruling: no visible path segments on the portal. Every screen
// renders at the bare origin, selected by the `do` query parameter; the old
// named paths (including the edge gate's /login target) normalize to the
// root with their query intact. An unauthenticated protected screen renders
// SignIn in place so the URL never grows a path.
function RequireSession({ children }: { children: JSX.Element }) {
  return getSession() ? children : <SignIn />;
}

function ScreenAtRoot() {
  const location = useLocation();
  const screen = new URLSearchParams(location.search).get('do');
  if (screen === 'security') return <Security />;
  if (screen === 'logout') return <Logout />;
  if (screen === 'tenant') return <RequireSession><TenantPicker /></RequireSession>;
  if (screen === 'switch') return <RequireSession><SwitchTenant /></RequireSession>;
  return isDeviceTrusted() ? <SignIn /> : getSession() ? <Home /> : <SignIn />;
}

function LegacyPathRedirect({ screen }: { screen?: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (screen) params.set('do', screen);
  const search = params.toString();
  return <Navigate to={{ pathname: '/', search: search ? `?${search}` : '' }} replace />;
}

export default function App() {
  useEffect(() => {
    const handleFocus = () => {
      const s = getSession();
      if (!s) return;
      const msUntilExpiry = s.expiresAt * 1000 - Date.now();
      if (msUntilExpiry < THIRTY_MIN_MS) {
        refreshSession().catch(() => {});
      }
    };
    window.addEventListener('focus', handleFocus);
    const id = window.setInterval(() => {
      if (getSession()) refreshSession().catch(() => {});
    }, THIRTY_MIN_MS);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.clearInterval(id);
    };
  }, []);

  return (
    <Routes>
      <Route path="/" element={<ScreenAtRoot />} />
      {/* The CloudFront edge gate still targets /login; it normalizes to the bare root. */}
      <Route path="/signin" element={<LegacyPathRedirect />} />
      <Route path="/login" element={<LegacyPathRedirect />} />
      <Route path="/logout" element={<LegacyPathRedirect screen="logout" />} />
      <Route path="/tenant" element={<LegacyPathRedirect screen="tenant" />} />
      <Route path="/switch" element={<LegacyPathRedirect screen="switch" />} />
      <Route path="*" element={<LegacyPathRedirect />} />
    </Routes>
  );
}
